import React, { memo } from "react";
import fp, { isEmpty } from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Field, change, formValueSelector } from "redux-form";

import {
	CheckButton,
	CustomInput,
	customMultiSelectStyles,
	IndicatorsContainer,
	MultiValueRemove,
	Select,
} from "components";
import { AchDesiredLimits } from "components/formSections";
import { DebounceTimeout } from "globalConstants";
import { useMonthsQuery } from "queries";
import { AmexOptBlueOrEsaTypes } from "types";
import {
	commaAndFloat2Normalizer,
	floatWithComma2Normalizer,
} from "validators";
import { required } from "validators/validation";

import styles from "./businessInfo.module.scss";

const DesiredLimits = ({
	formName,
	formValues,
	feesFormName,
	isEditForm,
	achProcessor,
}) => {
	const { data: months } = useMonthsQuery();
	const dispatch = useDispatch();
	const selector = formValueSelector(formName);
	const selectorFeesForm = feesFormName && formValueSelector(feesFormName);

	const isActiveAmexOptBlueOrEsaSectionValue = useSelector(
		fp.flow(
			state =>
				feesFormName &&
				selectorFeesForm(state, "isActiveAmexOptBlueOrEsaSection"),
		),
	);

	const amexOptBlueOrEsaFormValue = useSelector(
		fp.flow(
			state =>
				feesFormName &&
				selectorFeesForm(state, AmexOptBlueOrEsaTypes.AmexOptBlue.name),
		),
	);

	const updateMonths = months =>
		dispatch(change(formName, "seasonalMonths", months));

	const selectedMonths = useSelector(
		fp.flow(state => selector(state, "seasonalMonths")),
	);

	const isSeasonalChecked = useSelector(state =>
		selector(state, "seasonalChecked"),
	);

	const handleSetDefaultValuesForMonthlyFeesForm = () => {
		dispatch(change(feesFormName, "amexOtherItemFee", ""));
		dispatch(change(feesFormName, "amexOtherVolumeFee", ""));
		dispatch(change(feesFormName, "amexESANumber", ""));
		dispatch(
			change(
				feesFormName,
				AmexOptBlueOrEsaTypes.AmexOptBlue.name,
				AmexOptBlueOrEsaTypes.AmexOptBlue.value,
			),
		);

		dispatch(
			change(feesFormName, "isActiveAmexOptBlueOrEsaSection", false),
		);
	};

	const handleSetDefaultValueForAmexOptBlueEsa = (
		canAddDefaultValuesForMonthlyFeesForm,
		isIdenticalValue,
	) => {
		if (
			amexOptBlueOrEsaFormValue === AmexOptBlueOrEsaTypes.None.value ||
			(isEditForm &&
				!canAddDefaultValuesForMonthlyFeesForm &&
				!isIdenticalValue &&
				!isEmpty(amexOptBlueOrEsaFormValue))
		) {
			dispatch(
				change(
					feesFormName,
					AmexOptBlueOrEsaTypes.AmexOptBlue.name,
					AmexOptBlueOrEsaTypes.AmexOptBlue.value,
				),
			);
		}
	};

	const handleUpdateIsActiveAmexEsa = (
		canAddDefaultValuesForMonthlyFeesForm,
		previousValue,
		value,
	) => {
		if (
			(isActiveAmexOptBlueOrEsaSectionValue === false &&
				canAddDefaultValuesForMonthlyFeesForm === false &&
				+value > 0) ||
			(fp.isEmpty(previousValue) && +value !== 0) //  check has been added if user used data autocomplete
		) {
			return dispatch(
				change(feesFormName, "isActiveAmexOptBlueOrEsaSection", true),
			);
		}
	};

	const handleMonthlyAMEX = (_e, newValue, _previousValue) => {
		newValue = newValue.replace(/,/g, "");
		_previousValue = _previousValue.replace(/,/g, "");

		const canAddDefaultValues =
			feesFormName &&
			(isEmpty(formValues?.avgTicketAMEX) ||
				+formValues?.avgTicketAMEX === 0) &&
			(isEmpty(formValues?.highTicketAMEX) ||
				+formValues?.highTicketAMEX === 0) &&
			+_previousValue > 0;

		const isIdenticalValue = +newValue === +_previousValue;

		const canAddDefaultValuesForMonthlyFeesForm =
			(canAddDefaultValues && isEmpty(newValue)) ||
			(canAddDefaultValues && +newValue === 0);

		handleUpdateIsActiveAmexEsa(
			canAddDefaultValuesForMonthlyFeesForm,
			_previousValue,
			newValue,
		);

		handleSetDefaultValueForAmexOptBlueEsa(
			canAddDefaultValuesForMonthlyFeesForm,
			isIdenticalValue,
		);

		if (canAddDefaultValuesForMonthlyFeesForm) {
			handleSetDefaultValuesForMonthlyFeesForm();
		}

		if (+newValue >= 1000000) {
			dispatch(
				change(
					feesFormName,
					AmexOptBlueOrEsaTypes.ESA.name,
					AmexOptBlueOrEsaTypes.ESA.value,
				),
			);
		}
	};

	const handleAvgTicketAMEX = (_e, newValue, _previousValue) => {
		newValue = newValue.replace(/,/g, "");
		_previousValue = _previousValue.replace(/,/g, "");

		const canAddDefaultValues =
			feesFormName &&
			(isEmpty(formValues?.monthlyAMEX) ||
				+formValues?.monthlyAMEX === 0) &&
			(isEmpty(formValues?.highTicketAMEX) ||
				+formValues?.highTicketAMEX === 0) &&
			+_previousValue > 0;

		const isIdenticalValue = +newValue === +_previousValue;

		const canAddDefaultValuesForMonthlyFeesForm =
			(canAddDefaultValues && isEmpty(newValue)) ||
			(canAddDefaultValues && +newValue === 0);

		handleUpdateIsActiveAmexEsa(
			canAddDefaultValuesForMonthlyFeesForm,
			newValue,
		);
		handleSetDefaultValueForAmexOptBlueEsa(
			canAddDefaultValuesForMonthlyFeesForm,
			_previousValue,
			isIdenticalValue,
		);

		if (canAddDefaultValuesForMonthlyFeesForm) {
			handleSetDefaultValuesForMonthlyFeesForm();
		}
	};

	const handleHighTicketAMEX = (_e, newValue, _previousValue) => {
		newValue = newValue.replace(/,/g, "");
		_previousValue = _previousValue.replace(/,/g, "");

		const canAddDefaultValues =
			feesFormName &&
			(isEmpty(formValues?.monthlyAMEX) ||
				+formValues?.monthlyAMEX === 0) &&
			(isEmpty(formValues?.avgTicketAMEX) ||
				+formValues?.avgTicketAMEX === 0) &&
			+_previousValue > 0;

		const isIdenticalValue = +newValue === +_previousValue;

		const canAddDefaultValuesForMonthlyFeesForm =
			(canAddDefaultValues && isEmpty(newValue)) ||
			(canAddDefaultValues && +newValue === 0);

		handleUpdateIsActiveAmexEsa(
			canAddDefaultValuesForMonthlyFeesForm,
			_previousValue,
			newValue,
		);
		handleSetDefaultValueForAmexOptBlueEsa(
			canAddDefaultValuesForMonthlyFeesForm,
			isIdenticalValue,
		);

		if (canAddDefaultValuesForMonthlyFeesForm) {
			handleSetDefaultValuesForMonthlyFeesForm();
		}
	};

	return (
		<>
			<div className={`${styles.subHeader} mb-3`}>Desired Limits</div>
			<Row>
				<Col md={5}>
					<Field
						component={CustomInput}
						validate={[required]}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						label="Monthly Volume (V/MC/D), $"
						name="monthlyVMCD"
						placeholder="0.00"
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						validate={[required]}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						onChange={handleMonthlyAMEX}
						label="Monthly Volume (AMEX), $"
						name="monthlyAMEX"
						placeholder="0.00"
					/>
				</Col>
			</Row>
			<Row>
				<Col md={5}>
					<Field
						component={CustomInput}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						validate={[required]}
						label="Avg Ticket (V/MC/D), $"
						name="avgTicketVMCD"
						placeholder="0.00"
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						onChange={handleAvgTicketAMEX}
						validate={[required]}
						label="Avg Ticket (AMEX), $"
						name="avgTicketAMEX"
						placeholder="0.00"
					/>
				</Col>
			</Row>
			<Row>
				<Col md={5}>
					<Field
						component={CustomInput}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						validate={[required]}
						label="High Ticket (V/MC/D), $ "
						name="highTicketVMCD"
						placeholder="0.00"
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						solveCaretIssue={commaAndFloat2Normalizer}
						onChange={handleHighTicketAMEX}
						validate={[required]}
						label="High Ticket (AMEX), $"
						name="highTicketAMEX"
						placeholder="0.00"
					/>
				</Col>
			</Row>
			{achProcessor && (
				<AchDesiredLimits formValues={formValues} isRequired />
			)}
			<Row>
				<Col lg={5}>
					<Field
						component={CheckButton}
						label="Seasonal"
						name="seasonalChecked"
						id="seasonalChecked"
						hideRequiredMark
					/>

					<Select
						placeholder="Select Months"
						isMulti
						name="seasonalMonths"
						options={months}
						value={selectedMonths}
						onChange={updateMonths}
						getOptionLabel={option => option.name}
						defaultOptions
						debounceTimeout={DebounceTimeout}
						isDisabled={!isSeasonalChecked}
						closeMenuOnSelect={false}
						components={{
							IndicatorsContainer,
							MultiValueRemove,
						}}
						styles={customMultiSelectStyles}
						className="customMultiSelect"
						isClearable={false}
					/>
				</Col>
			</Row>
		</>
	);
};

export default memo(DesiredLimits);
